import { penaltyReminderScope } from '@bandido/shared/types';
import { PenaltyReminderSettingsButton } from '../../travel-services/components/PenaltyReminderSettingsButton';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import {
  RuteroConnectorStatus,
  useDeleteRuteroConnector,
  useRuteroConnectors,
} from '../queries';
import { RuteroConnectorFormModal } from './RuteroConnectorFormModal';

interface Props {
  agencyId: number;
}

/**
 * Catálogo de conectores de Rutero y su estado para la agencia. Un conector
 * sin credenciales no existe para la agencia (no participa de las búsquedas).
 */
export function RuteroConnectorsList({ agencyId }: Props) {
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const { data: connectors, isLoading, isError } = useRuteroConnectors(agencyId);
  const removeConnector = useDeleteRuteroConnector(agencyId);
  const [editing, setEditing] = useState<RuteroConnectorStatus | null>(null);
  const [deleting, setDeleting] = useState<RuteroConnectorStatus | null>(null);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (isError || !connectors) {
    return <Alert severity="error">{t('rutero.connectors.loadError')}</Alert>;
  }

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await removeConnector.mutateAsync({
        connector: deleting.connector,
        vertical: deleting.vertical,
      });
      addToast({ message: t('rutero.connectors.deleteSuccess'), color: 'success' });
      setDeleting(null);
    } catch {
      addToast({ message: t('rutero.connectors.deleteError'), color: 'error' });
    }
  };

  return (
    <Box>
      <Typography variant="h6">{t('rutero.connectors.title')}</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {t('rutero.connectors.intro')}
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('rutero.connectors.provider')}</TableCell>
            <TableCell>{t('rutero.connectors.connector')}</TableCell>
            <TableCell>{t('rutero.connectors.vertical')}</TableCell>
            <TableCell>{t('rutero.connectors.status')}</TableCell>
            <TableCell align="right">{t('rutero.connectors.actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {connectors.map((connector) => (
            <TableRow key={connector.connector} hover>
              <TableCell>{connector.provider}</TableCell>
              <TableCell>{connector.connector}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  variant="outlined"
                  label={t(`rutero.verticals.${connector.vertical}`)}
                />
              </TableCell>
              <TableCell>
                {connector.configured ? (
                  <Chip
                    size="small"
                    color={connector.enabled ? 'success' : 'default'}
                    label={
                      connector.enabled
                        ? t('rutero.connectors.configured')
                        : t('rutero.connectors.disabled')
                    }
                  />
                ) : (
                  <Chip size="small" label={t('rutero.connectors.notConfigured')} />
                )}
              </TableCell>
              <TableCell align="right">
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  {connector.configured && <PenaltyReminderSettingsButton agencyId={agencyId}
                    scope={penaltyReminderScope(agencyId, connector.vertical, connector.provider)} />}
                  <Button size="small" onClick={() => setEditing(connector)}>
                    {connector.configured
                      ? t('rutero.connectors.replace')
                      : t('rutero.connectors.configure')}
                  </Button>
                  {connector.configured && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => setDeleting(connector)}
                    >
                      {t('rutero.connectors.delete')}
                    </Button>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <RuteroConnectorFormModal
        agencyId={agencyId}
        connector={editing}
        onClose={() => setEditing(null)}
      />

      <Dialog open={Boolean(deleting)} onClose={() => setDeleting(null)}>
        <DialogTitle>{t('rutero.connectors.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('rutero.connectors.deleteConfirmBody', {
              connector: deleting?.connector,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>
            {t('rutero.connectors.cancel')}
          </Button>
          <LoadingButton
            color="error"
            variant="contained"
            loading={removeConnector.isLoading}
            onClick={handleDelete}
          >
            {t('rutero.connectors.delete')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
